// Helpers for events: date formatting, ICS download, share intents, RSVP questions.

export type RsvpQuestionType =
  | "short_text"
  | "long_text"
  | "yes_no"
  | "single_choice"
  | "multi_choice"
  | "dropdown"
  | "number"
  | "rating"
  | "date"
  | "email"
  | "phone";

export type RsvpQuestion = {
  id: string;
  label: string;
  type: RsvpQuestionType;
  required?: boolean;
  help?: string;
  options?: string[]; // for single_choice / multi_choice / dropdown
  min?: number;       // for number
  max?: number;       // for number
};

export type RsvpAnswerValue = string | string[] | number | null;
export type RsvpAnswers = Record<string, RsvpAnswerValue>;

export const QUESTION_TYPE_LABELS: Record<RsvpQuestionType, string> = {
  short_text: "Short text",
  long_text: "Long text",
  yes_no: "Yes / No",
  single_choice: "Single choice",
  multi_choice: "Multi choice",
  dropdown: "Dropdown",
  number: "Number",
  rating: "Rating (1–5)",
  date: "Date",
  email: "Email",
  phone: "Phone",
};

export function newQuestionId() {
  return "q_" + Math.random().toString(36).slice(2, 10);
}

export function emptyAnswerFor(q: RsvpQuestion): RsvpAnswerValue {
  if (q.type === "multi_choice") return [];
  if (q.type === "number" || q.type === "rating") return null;
  return "";
}

// Returns the first error message, or null when valid.
export function validateAnswer(q: RsvpQuestion, v: RsvpAnswerValue): string | null {
  const isEmpty =
    v === null ||
    v === undefined ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);
  if (q.required && isEmpty) return `${q.label} is required`;
  if (isEmpty) return null;

  switch (q.type) {
    case "short_text":
      if (typeof v !== "string" || v.length > 200) return `${q.label}: max 200 characters`;
      return null;
    case "long_text":
      if (typeof v !== "string" || v.length > 1000) return `${q.label}: max 1000 characters`;
      return null;
    case "email":
      if (typeof v !== "string" || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return `${q.label}: invalid email`;
      return null;
    case "phone":
      if (typeof v !== "string" || v.length > 40) return `${q.label}: invalid phone`;
      return null;
    case "number": {
      const n = typeof v === "number" ? v : Number(v);
      if (!Number.isFinite(n)) return `${q.label}: must be a number`;
      if (q.min !== undefined && n < q.min) return `${q.label}: min ${q.min}`;
      if (q.max !== undefined && n > q.max) return `${q.label}: max ${q.max}`;
      return null;
    }
    case "rating": {
      const n = typeof v === "number" ? v : Number(v);
      if (!Number.isFinite(n) || n < 1 || n > 5) return `${q.label}: choose 1–5`;
      return null;
    }
    case "yes_no":
      if (v !== "Yes" && v !== "No") return `${q.label}: choose Yes or No`;
      return null;
    case "single_choice":
    case "dropdown":
      if (typeof v !== "string" || !(q.options ?? []).includes(v)) return `${q.label}: invalid choice`;
      return null;
    case "multi_choice":
      if (!Array.isArray(v) || v.some((x) => !(q.options ?? []).includes(x))) return `${q.label}: invalid choice`;
      return null;
    case "date":
      if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${q.label}: invalid date`;
      return null;
  }
}

export function validateAnswers(questions: RsvpQuestion[], answers: RsvpAnswers): string | null {
  for (const q of questions) {
    const err = validateAnswer(q, answers[q.id] ?? emptyAnswerFor(q));
    if (err) return err;
  }
  return null;
}

export function formatAnswer(v: RsvpAnswerValue): string {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "string") return v.trim() === "" ? "—" : v;
  return String(v);
}

export type EventRow = {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  description: string | null;
  hero_image: string | null;
  starts_at: string;
  ends_at: string | null;
  timezone: string;
  location_name: string | null;
  location_address: string | null;
  is_virtual: boolean;
  virtual_url: string | null;
  status: string;
  is_featured: boolean;
  capacity: number | null;
  project_id: string | null;
  fundraiser_id: string | null;
  rsvp_enabled: boolean;
  donation_cta_label: string | null;
  share_blurb: string | null;
  rsvp_questions?: RsvpQuestion[] | any;
};

export function formatEventDate(starts_at: string, ends_at?: string | null) {
  const s = new Date(starts_at);
  const dateFmt = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timeFmt = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });
  if (!ends_at) return `${dateFmt.format(s)} · ${timeFmt.format(s)}`;
  const e = new Date(ends_at);
  const sameDay = s.toDateString() === e.toDateString();
  if (sameDay) return `${dateFmt.format(s)} · ${timeFmt.format(s)} – ${timeFmt.format(e)}`;
  return `${dateFmt.format(s)} ${timeFmt.format(s)} → ${dateFmt.format(e)} ${timeFmt.format(e)}`;
}

export function deriveStatus(ev: Pick<EventRow, "starts_at" | "ends_at" | "status">) {
  if (ev.status === "draft" || ev.status === "cancelled") return ev.status;
  const now = Date.now();
  const s = new Date(ev.starts_at).getTime();
  const e = ev.ends_at ? new Date(ev.ends_at).getTime() : s + 60 * 60 * 1000;
  if (now < s) return "scheduled";
  if (now <= e) return "live";
  return "past";
}

function pad(n: number) { return n.toString().padStart(2, "0"); }
function toIcsDate(d: Date) {
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

export function buildIcs(ev: EventRow, siteUrl: string) {
  const start = new Date(ev.starts_at);
  const end = ev.ends_at ? new Date(ev.ends_at) : new Date(start.getTime() + 60 * 60 * 1000);
  const loc = ev.is_virtual
    ? ev.virtual_url ?? "Online"
    : [ev.location_name, ev.location_address].filter(Boolean).join(", ");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//RaiseSriLanka//Events//EN",
    "BEGIN:VEVENT",
    `UID:${ev.id}@raisesrilanka.org`,
    `DTSTAMP:${toIcsDate(new Date())}`,
    `DTSTART:${toIcsDate(start)}`,
    `DTEND:${toIcsDate(end)}`,
    `SUMMARY:${escIcs(ev.title)}`,
    `DESCRIPTION:${escIcs((ev.summary ?? "") + "\\n\\n" + siteUrl + "/events/" + ev.slug)}`,
    `LOCATION:${escIcs(loc || "")}`,
    `URL:${siteUrl}/events/${ev.slug}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n");
}

function escIcs(s: string) {
  return s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
}

export function downloadIcs(ev: EventRow, siteUrl: string) {
  const blob = new Blob([buildIcs(ev, siteUrl)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${ev.slug}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

export function shareIntents(ev: EventRow, siteUrl: string) {
  const url = `${siteUrl}/events/${ev.slug}`;
  const text = ev.share_blurb ?? `Join us: ${ev.title}`;
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  return {
    twitter: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    whatsapp: `https://wa.me/?text=${t}%20${u}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    email: `mailto:?subject=${encodeURIComponent(ev.title)}&body=${t}%20${u}`,
  };
}
